'use client'

import { usePathname } from 'next/navigation'

import Link, { type LinkProps } from 'next/link'

import { ArrowLeft1Regular } from '@/icons/web/regular/arrow-left-1'
import { ArrowRight1Regular } from '@/icons/web/regular/arrow-right-1'

import { type ReactNode } from 'react'
import { usePaginationContext } from './pagination-context'
import { isNullOrEmpty } from '@/utils/string-extensions'

const disabledColor = '#E2E5EA'
const activeColor = '#08033B'

const DisabledButton = {
  color: `${disabledColor}`,
  background: 'transparent',
  border: '1px solid #FAFBFC',
  cursor: 'default',
}

interface PageLinkProps extends LinkProps {
  page: number
  rel: 'prev' | 'next'
  isEnabled: boolean
  searchParams?: Record<string, string>
}

const PageLink = ({
  page,
  isEnabled,
  children,
  searchParams,
  ...props
}: Omit<PageLinkProps, 'href'> & { children: ReactNode }) => {
  const { urlHash } = usePaginationContext()

  const params = new URLSearchParams()

  for (const key in searchParams) {
    params.set(key, searchParams[key])
  }

  // https://gitlab.com/lasoodevs/digital/frontend/fe-lasoo-consumer-next/-/issues/75
  if (page > 1) {
    params.set('page', page.toString())
  } else {
    params.delete('page')
  }

  const hash = isNullOrEmpty(urlHash) ? '' : `#${urlHash}`
  const paramsString = isNullOrEmpty(params.toString())
    ? ''
    : `?${params.toString()}`
  const pathname = usePathname()
  const url = `${pathname}${paramsString}${hash}`

  return (
    <Link prefetch={false} className='mx-4' {...props} href={url}>
      {children}
    </Link>
  )
}

interface PaginatorControlProps {
  currentPage: number
  numberOfPages: number
  direction: 'prev' | 'next'
  searchParams?: Record<string, string>
}

const PaginatorControl = ({
  currentPage,
  numberOfPages,
  direction,
  searchParams,
}: PaginatorControlProps) => {
  const isBackwards = direction === 'prev'
  const pageTo = isBackwards ? currentPage - 1 : currentPage + 1
  const isEnabled = pageTo > 0 && pageTo <= numberOfPages

  const color = isEnabled ? activeColor : disabledColor

  const icon = isBackwards ? (
    <ArrowLeft1Regular height={20} width={20} color={color} stroke={color} />
  ) : (
    <ArrowRight1Regular height={20} width={20} color={color} stroke={color} />
  )

  return (
    <PageLink
      page={isEnabled ? pageTo : currentPage}
      isEnabled={isEnabled}
      rel={direction}
      searchParams={searchParams}
    >
      <button
        className='flex cursor-pointer rounded-[3px] border border-solid border-primary-dark bg-primary-contrast-text px-5 py-[5px] text-lg'
        style={{
          ...(!isEnabled && DisabledButton),
        }}
        data-testid={
          isBackwards ? 'paginator-prev-button' : 'paginator-next-button'
        }
      >
        {icon}
      </button>
    </PageLink>
  )
}

interface PaginatorProps {
  currentPage: number
  numberOfPages: number
  searchParams?: Record<string, string>
}

export const Paginator = ({
  currentPage,
  numberOfPages,
  searchParams,
}: PaginatorProps) => {
  return (
    <>
      <div className='h-12' />
      <div
        className='flex flex-row items-center justify-center'
        data-testid='pagination-container'
      >
        <PaginatorControl
          currentPage={currentPage}
          numberOfPages={numberOfPages}
          direction='prev'
          searchParams={searchParams}
        />
        <div className='mx-4 text-sm sm:text-base'>
          Page {currentPage} of {numberOfPages}
        </div>
        <PaginatorControl
          currentPage={currentPage}
          numberOfPages={numberOfPages}
          direction='next'
          searchParams={searchParams}
        />
      </div>
    </>
  )
}
